'use client';

import { useState, useCallback, useRef } from 'react';
import { GraphPoint } from '@/types';

interface TooltipData {
  visible: boolean;
  x: number;
  y: number;
  point: GraphPoint | null;
  pointIndex: number;
}

export const useGraphTooltip = (processedPoints: GraphPoint[]) => {
  const [tooltip, setTooltip] = useState<TooltipData>({
    visible: false,
    x: 0,
    y: 0,
    point: null,
    pointIndex: 0
  });

  const svgRef = useRef<SVGSVGElement>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || processedPoints.length === 0) return;

    const rect = svgRef.current.getBoundingClientRect();
    const svgWidth = parseFloat(svgRef.current.getAttribute('viewBox')?.split(' ')[2] || '400');
    const x = ((e.clientX - rect.left) / rect.width) * svgWidth;

    // Find closest point
    let closestPoint: GraphPoint | null = null;
    let minDistance = Infinity;
    let closestIndex = 0;

    for (let i = 0; i < processedPoints.length; i++) {
      const point = processedPoints[i];
      const distance = Math.abs(point.x - x);
      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = point;
        closestIndex = i;
      }
    }

    if (closestPoint && minDistance < 20) {
      setTooltip({
        visible: true,
        x: e.clientX + 10,
        y: e.clientY - 10,
        point: closestPoint,
        pointIndex: closestIndex
      });
    } else {
      setTooltip(prev => ({ ...prev, visible: false }));
    }
  }, [processedPoints]);

  const handleMouseLeave = useCallback(() => {
    setTooltip(prev => ({ ...prev, visible: false }));
  }, []);

  return {
    tooltip,
    svgRef,
    handleMouseMove,
    handleMouseLeave
  };
};
